import React, { ReactNode, FC, useContext, useMemo, MouseEvent } from 'react';
import { ThemeContext } from 'styled-components';
import { T } from 'ia-react-core/~/interfaces/t';
import findChildByType from '../../../utils/components/#findChildByType';
import Col from '../../grid/Col';
import MainHeaderLayout from './#MainHeaderLayout';
import { ReactComponent as CloseIcon } from '../../../icons/close.svg';
import MainLogo from '../MainLogo';
import MainLogoHorizontal from '../MainLogoHorizontal';
import MainLanguageButton from '../../buttons/MainLanguageButton';
import { StyledLogoCol, StyledRightContentCol, StyledClosedButton, StyledAppName, StyledCloseIcon, StyledClosedButtonText, StyledRightRow, StyledMiddleContent } from './MainHeader.styles';
import { LeftContent, MiddleContent, RightContent } from './MainHeader.compounds';

export interface MainHeaderProps {
  /** The content of the header. Use `MainHeader.RightContent` to customize the content to the right of the header. */
  children?: ReactNode;
  /** Lets you modify the home URL that wrap the main logo */
  homePageUrl?: string;
  /** App name to display next to the logo. */
  appName?: string;
  /** Use the primary color background. */
  primary?: boolean;
  /** Use the horizontal variant of the logo. */
  horizontalLogo?: boolean;
  /** Removes the language button */
  noLanguageButton?: boolean;
  /** Remove the logo */
  noLogo?: boolean;
  /** Controls the padding of the header. */
  padding?: string;
  /** Controls the padding of the header on the "md" breakpoint. */
  mdPadding?: string;
  /** Overrides the height of the height. */
  height?: number;
  /** Overrides the css background. */
  background?: string;
  /** Callback when the X button is clicked. The button is only displayedif this option is defined. */
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Callback triggered when the language button is clicked  @param {string} nextLanguage The next language code */
  onLanguageChange?: (newLocale: string) => void;
}

interface MainHeaderInternalProps {
  t: T;
  tReady: boolean;
}

interface MainHeaderComposedProps extends MainHeaderProps, MainHeaderInternalProps{}

export type MainHeaderStatics = {
  LeftContent: typeof LeftContent;
  MiddleContent: typeof LeftContent;
  RightContent: typeof RightContent;
};

const MainHeaderDefaultProps = {
  horizontalLogo: false,
  primary: false,
};

/**
 * @since 0.14.0
 */
const MainHeader: FC<MainHeaderComposedProps> & MainHeaderStatics = ({
  children, t, tReady, height, appName, primary, horizontalLogo, onLanguageChange, homePageUrl, noLanguageButton, noLogo, padding, mdPadding, onClose, background, ...props
}) => {
  const theme = useContext(ThemeContext);
  const leftContent = findChildByType(children, LeftContent);
  const rightContent = findChildByType(children, RightContent);
  const middleContent = findChildByType(children, MiddleContent);

  const resolvedNoLanguageButton = useMemo(() => {
    if (typeof noLanguageButton !== 'undefined') {
      return noLanguageButton;
    }
    return theme.layout.noHeaderLanguageButton;
  }, [noLanguageButton, theme.layout.noHeaderLanguageButton]);

  const resolvedNoLogo = useMemo(() => {
    if (typeof noLogo !== 'undefined') {
      return noLogo;
    }

    return theme.layout.noLogo;
  }, [noLogo, theme.layout.noLogo]);

  return (
    <MainHeaderLayout
      height={height}
      primary={primary}
      horizontalLogo={horizontalLogo}
      padding={padding}
      mdPadding={mdPadding}
      background={background}
      data-testid="MainHeader"
      {...props}
    >
      {middleContent && (
        <StyledMiddleContent primary={primary}>
          {middleContent}
        </StyledMiddleContent>
      )}
      {leftContent && (
        <Col xs="content" m-l-xs={-16} m-l-md={0}>
          {leftContent}
        </Col>
      )}
      {!resolvedNoLogo && (
        <Col xs="content">
          {!horizontalLogo && <MainLogo homePageUrl={homePageUrl} />}
          {horizontalLogo && <MainLogoHorizontal homePageUrl={homePageUrl} isInverted={primary} />}
        </Col>
      )}
      <StyledLogoCol>
        {appName && (
          <StyledAppName
            horizontalLogo={horizontalLogo}
            primary={primary}
          >
            {appName}
          </StyledAppName>
        )}
      </StyledLogoCol>
      <Col xs="content" m-r-xs={-16} m-r-md={0}>
        <StyledRightRow role="navigation">
          {rightContent && (
            <StyledRightContentCol>
              {rightContent}
            </StyledRightContentCol>
          )}
          {!resolvedNoLanguageButton && (
            <Col>
              <MainLanguageButton
                data-id="MainHeaderLanguageButton"
                onLanguageChange={onLanguageChange}
              />
            </Col>
          )}
          {onClose && (
            <Col>
              <StyledClosedButton
                type="button"
                primary={primary}
                data-testid="MainHeaderCloseButton"
                onClick={onClose}
              >
                <StyledClosedButtonText>{t('closeButton')}</StyledClosedButtonText>
                <StyledCloseIcon as={CloseIcon} />
              </StyledClosedButton>
            </Col>
          )}
        </StyledRightRow>
      </Col>
    </MainHeaderLayout>
  );
};

MainHeader.defaultProps = MainHeaderDefaultProps;
MainHeader.LeftContent = LeftContent;
MainHeader.RightContent = RightContent;
MainHeader.MiddleContent = MiddleContent;

export default MainHeader;
